import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { formatLastRefreshLabel } from 'utils/helperFns'
import { setAlertQueue } from 'cache/alerts'
import { selectedApprovalRowsVar } from 'cache/approvals'
import useAuthHook from 'hooks/use-auth'
import { usePendingApprovals } from 'hooks/use-pending-approvals'
import { useUserProfile } from 'hooks/use-user-profile'
import { MessageType } from 'ts/enums/messageType.enums'
import { ApprovalsTable } from './ApprovalsTable'
import ApprovalsToolbar from './ApprovalsToolbar/ApprovalsToolbar'
import styles from './styles.module.scss'

const UPDATE_INTERVAL_MS = 60000 // 1 minute

const ApprovalsPage: React.FC = () => {
  const { user } = useAuthHook()
  const { userId } = useUserProfile()
  const { getPendingApprovals, pendingApprovals, loading } = usePendingApprovals()
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null)
  const [lastManualRefreshAt, setLastManualRefreshAt] = useState<number | null>(null)
  const [now, setNow] = useState<number>(() => Date.now())
  const givenName = user?.profile?.givenName
  const greetingName =
    typeof givenName === 'string' && givenName.trim().length > 0 ? givenName : 'User'

  const refreshApprovals = useCallback(
    async (isManual: boolean) => {
      if (!userId) {
        return
      }

      try {
        const refreshResult = await getPendingApprovals(userId)
        const didError = refreshResult?.didError ?? true
        selectedApprovalRowsVar([])

        const now = Date.now()
        setLastRefreshAt(now)
        if (isManual) {
          setLastManualRefreshAt(now)

          if (!didError) {
            setAlertQueue({
              type: MessageType.SUCCESS,
              message: 'Data refreshed',
            })
          }
        }
      } catch (error) {
        console.error('Failed to refresh approvals:', error)
        setAlertQueue({
          type: MessageType.ERROR,
          message: 'Failed to refresh approvals. Please try again.',
        })
      }
    },
    [getPendingApprovals, userId]
  )

  useEffect(() => {
    if (!userId) {
      return
    }

    const runInitialRefresh = async () => {
      await refreshApprovals(false)
    }

    runInitialRefresh()
  }, [refreshApprovals, userId])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now())
    }, UPDATE_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [])

  const lastRefreshLabel = useMemo(() => {
    return formatLastRefreshLabel(lastRefreshAt, now)
  }, [lastRefreshAt, now])

  return (
    <Box className={styles.outerContainer}>
      <Box className={styles.innerContainer}>
        <div className={styles.greeting}>{`Hello, ${greetingName}!`}</div>
        <div className={styles.subtitle}>
          Please see the list of deals that have pending action.
        </div>
        <ApprovalsToolbar
          onRefresh={() => {
            refreshApprovals(true)
          }}
          lastRefreshLabel={lastRefreshLabel}
          loading={loading}
        />
        <ApprovalsTable
          pendingApprovals={pendingApprovals}
          loading={loading}
          lastManualRefreshAt={lastManualRefreshAt}
        />
      </Box>
    </Box>
  )
}

export default ApprovalsPage
